import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { AuthorsCollab, CareTeam } from '../../assets/components/svgs';
import Card from '../shared/cards/Card';
import {
  setCareTeamsList,
  setCaregiverList,
  setConditionsList,
  setDeviceList,
  setListData,
  setMedicationsList,
  setOrgList,
  setPatientUserList,
  setPractitionerList,
  setPractitionerRoleList,
  setSurveyList
} from '../../app/carePlanReducer';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';
import { AppConstants } from '../../constants/AppConstants';
import {
  fetchDeviceList,
  fetchCustomRoleUserList,
  fetchPractitionerRole,
  filterAuthorData,
  generateRandomKey,
  getCarePlanConditions,
  getCarePlanOrganizations,
  getCareTeamsList,
  fetchMedicationsList,
  fetchSurveyList,
  getCareplanListData,
  formatContributerPills
} from './carePlanUtils';
import { userFilterQuery } from '../../util/admin-utils';
import { getUsers } from '../../http/user-management-services';
import { HTTP_STATUS } from '../../http/constants/http.status';
import Pills from './Reusable/Pills';
import { cloneDeep, findIndex, get, isObject, uniqBy } from 'lodash';
import styled from 'styled-components/macro';
import PickListWithApiSearch from '../shared/Fields/PickListWithAPISearch';
import { PlusCircleIcon } from '@heroicons/react/outline';
import { IconButton } from '../primitives/buttons/IconButton';
import { useTheme } from '../../context/themeContext';

interface KeyVal {
  key: string;
  value: string;
}
interface Props {
  formData: any;
  setFormData: any;
  disabled: boolean;
  formError: any;
  primaryFieldLabel: string;
  primaryFieldKey?: string;
  secondaryFieldLabel: string;
  secondaryFieldKey?: string;
  primaryFieldOptions: Array<KeyVal>;
  setFormError: any;
  multiSupport?: boolean;
  multiFieldKey?: string;
}

export const ErrorLabel = styled.span`
  fontsize: 11px;
  color: red;
`;

const DependentSelector = ({
  formData,
  setFormData,
  disabled,
  formError,
  primaryFieldLabel = '',
  primaryFieldKey = '',
  secondaryFieldLabel = '',
  secondaryFieldKey = '',
  primaryFieldOptions = [],
  setFormError,
  multiSupport = false,
  multiFieldKey = ''
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const listData: any = useAppSelector((state) => state.carePlanData.listData);

  const [options, setOptions] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [renderKey, setRenderKey] = useState<number>(generateRandomKey());
  const [primaryFieldValue, setPrimaryFieldValue] = useState([]);
  const [secondaryFieldValue, setSecondaryFieldValue] = useState([]);

  const formatDataDisplayKey = (key: string, data: any) => {
    return data.map((item: any) => {
      const codeText = get(item, key);
      if (codeText) {
        return { ...item, display: codeText };
      } else {
        return item;
      }
    });
  };

  const fetchDataAndUpdateOptions = async (selection: any) => {
    const { key, apiConfig } = selection || {};

    const { fieldValues } = apiConfig || {};

    if (!listData?.[key]?.length) {
      const data = await getCareplanListData(apiConfig);
      if (data.length) {
        dispatch(setListData({ [key]: data }));
      }
      setOptions(
        fieldValues?.[0].includes('.') ? formatDataDisplayKey(fieldValues?.[0], data) : data
      );
      setLoading(false);
      setRenderKey(generateRandomKey());
    } else {
      setOptions(
        fieldValues?.[0].includes('.')
          ? formatDataDisplayKey(fieldValues?.[0], listData?.[key])
          : listData?.[key]
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData?.[primaryFieldKey]?.length || primaryFieldValue?.length) {
      setLoading(true);
      let [selection] = formData?.[primaryFieldKey]?.length
        ? formData?.[primaryFieldKey]
        : primaryFieldValue;
      fetchDataAndUpdateOptions(selection);
    }
  }, [formData?.[primaryFieldKey], primaryFieldValue]);

  const getApiAttribute = (keyName: string) => {
    const [ele] = formData?.[primaryFieldKey]?.length
      ? formData?.[primaryFieldKey]
      : primaryFieldValue;
    if (['key', 'display'].includes(keyName)) {
      return ele?.[keyName];
    } else {
      return ele?.apiConfig?.[keyName] || '';
    }
  };

  const handleSearch = (text: string) => {
    let data = cloneDeep(listData[getApiAttribute('key')]);
    const filteredResponse: any = filterAuthorData(
      getApiAttribute('fieldValues')[0]?.includes('.')
        ? formatDataDisplayKey(getApiAttribute('fieldValues')?.[0], data)
        : data,
      text,
      getApiAttribute('fieldValues')[0]?.includes('.')
        ? ['display']
        : getApiAttribute('fieldValues')
    );
    setOptions(filteredResponse);
  };

  const addMultiData = () => {
    let clonedItems = [...(formData?.[multiFieldKey] || [])];
    clonedItems = uniqBy(
      [
        ...clonedItems,
        formatContributerPills({ id: secondaryFieldValue, type: primaryFieldValue })
      ],
      'id'
    );
    setFormData({ [multiFieldKey]: clonedItems });
    // setPrimaryFieldValue([]);
    setSecondaryFieldValue([]);
  };

  const isAddButtonDisabled = () => {
    return !primaryFieldValue.length || !secondaryFieldValue.length;
  };

  return (
    <div className="flex w-full flex-wrap">
      <div className="mb-5 flex w-full">
        <div className="flex w-1/2 flex-col">
          <PickList
            fieldKey={'key'}
            fieldValues={['display']}
            label={primaryFieldLabel}
            selectedItems={multiSupport ? primaryFieldValue : formData?.[primaryFieldKey]}
            showAddOption={false}
            onSelectedChange={(items: any) => {
              if (getApiAttribute('key') !== items?.[0]?.key) {
                setOptions([]);
              }

              if (!items.length) {
                setOptions([]);
                if (multiSupport) {
                  setPrimaryFieldValue([]);
                  setSecondaryFieldValue([]);
                } else {
                  setFormData({ [primaryFieldKey]: [], [secondaryFieldKey]: [] });
                }

                return;
              }
              if (multiSupport) {
                setPrimaryFieldValue(items);
                setSecondaryFieldValue([]);
                return;
              }

              setFormData({ [primaryFieldKey]: items, [secondaryFieldKey]: [] });
            }}
            items={primaryFieldOptions}
            disabled={disabled}
            readOnly={false}
            type={PICKLIST_TYPES.SINGLE}
          />
        </div>
        <div className="flex w-1/2 flex-row justify-center pl-5">
          <div className="flex w-full flex-col">
            <PickList
              key={`${getApiAttribute('key')}-${renderKey}`}
              loading={loading}
              searchable
              displayDelimiter=" "
              searchData={handleSearch}
              fieldKey={getApiAttribute('fieldKey')}
              fieldValues={
                getApiAttribute('fieldValues')?.[0]?.includes('.')
                  ? ['display']
                  : getApiAttribute('fieldValues')
              }
              label={secondaryFieldLabel}
              selectedItems={
                getApiAttribute('fieldValues')?.[0]?.includes('.')
                  ? formatDataDisplayKey(
                      getApiAttribute('fieldValues')?.[0],
                      (() =>
                        !multiSupport
                          ? formData?.[secondaryFieldKey]
                          : secondaryFieldValue)()?.filter((element: any) => element) || []
                    )
                  : (() =>
                      !multiSupport
                        ? formData?.[secondaryFieldKey]
                        : secondaryFieldValue)()?.filter((element: any) => element) || []
              }
              onSelectedChange={(item: any) => {
                if (multiSupport) {
                  setSecondaryFieldValue(item);
                  return;
                }
                setFormData({ [secondaryFieldKey]: item });
                setFormError({ ...formError, [secondaryFieldKey]: '' });
              }}
              items={options || []}
              disabled={disabled}
              readOnly={false}
              type={PICKLIST_TYPES.SINGLE}
            />
            {formError?.[secondaryFieldKey] && (
              <ErrorLabel>{formError?.[secondaryFieldKey]}</ErrorLabel>
            )}
          </div>
          {multiSupport ? (
            <IconButton
              className={`ml-4 mt-6 h-7 w-7 cursor-pointer self-center border-none text-gray-600 hover:text-black ${
                isAddButtonDisabled() ? 'pointer-events-none cursor-not-allowed' : ''
              }`}
              onClick={addMultiData}
              disabled={isAddButtonDisabled()}
            >
              <PlusCircleIcon
                data-testid="add-attachment"
                color={
                  isAddButtonDisabled()
                    ? useTheme().theme.disabledColor
                    : useTheme().theme.primaryAppColor
                }
              />
            </IconButton>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );
};

export default DependentSelector;
